import { useState, useEffect, useRef, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { usePilotMode } from "@/hooks/use-pilot-mode";
import { queryClient } from "@/lib/queryClient";
import {
  Loader2, Send, Sparkles, ExternalLink, ArrowRight,
  CheckCircle2, Linkedin, Globe, ChevronDown,
} from "lucide-react";
import { SiX, SiMeta, SiFacebook } from "react-icons/si";

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  ts: string;
  agentVoice?: string;
  actions?: ChannelAction[];
}

interface ChannelAction {
  type: string;
  platform: string;
  label: string;
  url: string;
}

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  linkedin: Linkedin,
  x: SiX,
  meta: SiMeta,
  facebook: SiFacebook,
  google: Globe,
  instagram_business: Globe,
};

const PLATFORM_COLORS: Record<string, string> = {
  linkedin: "text-blue-400 border-blue-500/30 bg-blue-500/10",
  x: "text-white border-white/30 bg-white/10",
  meta: "text-blue-500 border-blue-600/30 bg-blue-600/10",
  facebook: "text-blue-500 border-blue-600/30 bg-blue-600/10",
  google: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  instagram_business: "text-pink-400 border-pink-500/30 bg-pink-500/10",
};

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
        <Sparkles size={14} className="text-blue-400" />
      </div>
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl rounded-tl-md px-4 py-3">
        <div className="flex gap-1 items-center h-4">
          <span className="w-1.5 h-1.5 bg-blue-400/60 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="w-1.5 h-1.5 bg-blue-400/60 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="w-1.5 h-1.5 bg-blue-400/60 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

function ChannelConnectCard({ action }: { action: ChannelAction }) {
  const [connected, setConnected] = useState(false);
  const Icon = PLATFORM_ICONS[action.platform] || Globe;
  const colorClass = PLATFORM_COLORS[action.platform] || "text-muted-foreground border-border bg-muted/20";

  return (
    <div className={`mt-2 flex items-center gap-3 px-3 py-2.5 rounded-xl border ${colorClass} max-w-xs`}>
      <Icon size={16} className="shrink-0" />
      <span className="text-sm font-medium flex-1">{action.label}</span>
      {connected ? (
        <CheckCircle2 size={14} className="text-green-400 shrink-0" />
      ) : (
        <a
          href={action.url}
          target="_blank"
          rel="noreferrer"
          onClick={() => setTimeout(() => setConnected(true), 3000)}
          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide hover:opacity-80 transition-opacity shrink-0"
          data-testid={`button-connect-${action.platform}`}
        >
          Connect <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
}

// A connect link is shown as a Connect card, never as a raw "/api/oauth/..." path in the text. A resumed chat (history from the
// server carries no actions) gets its cards back from the links in the text.
const OAUTH_LINK = /\/api\/oauth\/([^?/\s]+)\/authorize\?wsId=\d+/;
const CHANNEL_LABELS: Record<string, string> = {
  linkedin: "LinkedIn", x: "X (Twitter)", meta: "Meta (Facebook & Instagram)", instagram_business: "Instagram Business",
};

function actionsFromText(text: string): ChannelAction[] {
  const seen = new Set<string>();
  const out: ChannelAction[] = [];
  for (const m of Array.from(text.matchAll(new RegExp(OAUTH_LINK.source, "g")))) {
    if (seen.has(m[1])) continue;
    seen.add(m[1]);
    out.push({ type: "channel_connect", platform: m[1], label: CHANNEL_LABELS[m[1]] || m[1], url: m[0] });
  }
  return out;
}

function renderMessageText(text: string): React.ReactNode {
  const cleaned = text.replace(/ONBOARDING_COMPLETE/g, "").trim();
  const lines = cleaned.split("\n").filter((line) => !OAUTH_LINK.test(line));
  return lines.map((line, i) => {
    if (line.startsWith("• ") || line.startsWith("- ") || line.startsWith("→ ")) {
      return (
        <div key={i} className="flex gap-2">
          <span className="text-blue-400/60 shrink-0 mt-0.5">›</span>
          <span>{line.slice(2)}</span>
        </div>
      );
    }
    if (line.trim() === "") return <div key={i} className="h-1" />;
    return <div key={i}>{line}</div>;
  });
}

function DraperMessage({ msg }: { msg: ChatMessage }) {
  const actions = msg.actions && msg.actions.length > 0 ? msg.actions : actionsFromText(msg.text);
  return (
    <div className="flex items-end gap-2.5" data-testid={`msg-draper-${msg.ts}`}>
      <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center shrink-0">
        <Sparkles size={14} className="text-blue-400" />
      </div>
      <div className="max-w-[78%]">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Draper</span>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl rounded-tl-md px-4 py-3 text-sm text-foreground/85 leading-relaxed space-y-0.5">
          {renderMessageText(msg.text)}
        </div>
        {actions.length > 0 && (
          <div className="mt-1.5 space-y-1.5">
            {actions.map((action, i) => (
              <ChannelConnectCard key={i} action={action} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserMessage({ msg }: { msg: ChatMessage }) {
  return (
    <div className="flex justify-end" data-testid={`msg-user-${msg.ts}`}>
      <div className="max-w-[78%] bg-blue-600/20 border border-blue-500/20 rounded-2xl rounded-tr-md px-4 py-3 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">
        {msg.text}
      </div>
    </div>
  );
}

function PaywallModal({ onClose, onSkip }: { onClose: () => void; onSkip: () => void }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState<number | null>(null);

  const { data: packages = [], isLoading: packagesLoading } = useQuery<any[]>({ queryKey: ["/api/packages"] });

  // POST /api/stripe/checkout takes { packageId } (a saas_packages row id); only real packages can be checked out
  async function startCheckout(packageId: number) {
    setLoading(packageId);
    try {
      const r = await fetch("/api/stripe/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ packageId }),
      });
      const data = await r.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(data.message || "No checkout URL");
    } catch {
      toast({ title: "Checkout unavailable", description: "Try again or skip to explore the app.", variant: "destructive" });
      setLoading(null);
    }
  }

  const plans = (Array.isArray(packages) ? packages : []).filter((p: any) => Number.isInteger(Number(p?.id)) && Number(p.id) > 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="paywall-modal">
      <div className="max-w-2xl w-full bg-[#0a0a0a] border border-white/10 rounded-2xl p-8 shadow-2xl">
        <div className="text-center mb-8">
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/25 font-bold mb-3">Ready to Go Live</p>
          <h2 className="text-3xl font-black uppercase tracking-tight text-white mb-2">Choose Your Plan</h2>
          <p className="text-sm text-white/40">Your AI marketing team is built. Activate it with a subscription.</p>
        </div>
        {!packagesLoading && plans.length === 0 && (
          <p className="text-center text-sm text-white/40 mb-6" data-testid="paywall-no-plans">Plans are not available yet. You can explore the app now and subscribe later from Settings.</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          {plans.map((pkg: any) => (
            <div key={pkg.id || pkg.name} className="border border-white/10 p-6 hover:border-cyan-400/40 transition-all rounded-xl" data-testid={`paywall-plan-${pkg.id}`}>
              <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-white/40 mb-2">{pkg.name}</h3>
              <div className="text-4xl font-black text-white mb-5 flex items-baseline gap-1">
                <span className="text-lg text-white/50">$</span>
                {Math.floor(pkg.price || 79)}
                <span className="text-xs text-white/30 font-normal ml-1">/mo</span>
              </div>
              <ul className="space-y-2 mb-5">
                {(pkg.features || []).slice(0, 4).map((f: string, i: number) => (
                  <li key={i} className="text-xs text-white/50 flex items-start gap-2">
                    <span className="text-cyan-400 shrink-0">[›]</span> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`button-paywall-plan-${pkg.id}`}
                onClick={() => startCheckout(Number(pkg.id))}
                disabled={loading !== null}
                className="w-full py-2.5 border border-white/20 hover:border-cyan-400 hover:bg-cyan-400/10 transition-all uppercase font-bold text-xs tracking-widest rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading === Number(pkg.id) ? <Loader2 size={12} className="animate-spin" /> : null}
                Activate
              </button>
            </div>
          ))}
        </div>
        <div className="text-center space-y-2">
          <button
            data-testid="button-paywall-skip"
            onClick={onSkip}
            className="text-[11px] text-white/20 hover:text-white/40 transition-colors uppercase tracking-widest"
          >
            Skip for now — I'll subscribe later
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingChat({ onComplete }: { onComplete: () => void }) {
  const { toast } = useToast();
  const { setActiveWorkspaceId } = useWorkspaceStore();
  const pilotMode = usePilotMode(); // PILOT_MODE: never show the plans / checkout paywall
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [workspaceId, setWorkspaceId] = useState<number | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const initialized = useRef(false);

  const startMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/onboarding/start", {});
      return res.json();
    },
    onSuccess: (data: any) => {
      setWorkspaceId(data.workspaceId);
      setActiveWorkspaceId(data.workspaceId);
      if (data.complete) {
        // finished already (e.g. in another tab): straight into the app
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
        onComplete();
        return;
      }
      const msgs: ChatMessage[] = (data.messages || []).map((m: any) => ({
        role: m.role,
        text: m.text || "",
        ts: m.ts || new Date().toISOString(),
        agentVoice: m.agentVoice,
        actions: Array.isArray(m.metadata?.channelActions) ? m.metadata.channelActions : [],
      }));
      setMessages(msgs);
    },
    onError: (e: any) => {
      setStartError(e.message || "Failed to start onboarding. Please refresh the page.");
    },
  });

  const sendMutation = useMutation({
    mutationFn: async (message: string) => {
      const res = await apiRequest("POST", "/api/onboarding/message", {
        workspaceId,
        message,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      setIsTyping(false);
      const agentMsg: ChatMessage = {
        role: "assistant",
        text: data.text || "",
        ts: new Date().toISOString(),
        agentVoice: "draper",
        actions: data.actions || [],
      };
      setMessages(prev => [...prev, agentMsg]);
      if (data.isComplete) {
        // the workspaces list is refreshed when the owner leaves this screen (onComplete), not now: a refresh here would
        // switch straight into the app and hide the "what happens next" panel
        setIsComplete(true);
      }
    },
    onError: (e: any) => {
      setIsTyping(false);
      const msg: string = e?.message || "";
      if (msg.startsWith("402:") || msg.includes("onboarding_credits_exhausted") || msg.includes("credits are used up")) {
        if (pilotMode) {
          toast({ title: "Setup credits used up", description: "Your pilot's setup allowance is used up. Contact the DrPR team to continue." });
          return;
        }
        setShowPaywall(true);
        return;
      }
      toast({
        title: "Something went wrong",
        description: msg || "Draper had an issue. Try again.",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      startMutation.mutate();
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const sendMessage = useCallback(() => {
    const text = input.trim();
    if (!text || !workspaceId || sendMutation.isPending || isTyping) return;

    const userMsg: ChatMessage = {
      role: "user",
      text,
      ts: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    setIsTyping(true);
    sendMutation.mutate(text);
  }, [input, workspaceId, sendMutation, isTyping]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  async function handleGoLive() {
    if (pilotMode) {
      // pilot: no subscription step — straight into the app
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      onComplete();
      return;
    }
    try {
      const r = await fetch("/api/onboarding/credit-status", { credentials: "include" });
      if (r.ok) {
        const { isPaid } = await r.json();
        if (isPaid) {
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
          onComplete();
          return;
        }
      }
    } catch {}
    setShowPaywall(true);
  }

  function handleSkipPaywall() {
    setShowPaywall(false);
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
    onComplete();
  }

  if (startMutation.isPending && messages.length === 0) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center" data-testid="onboarding-loading">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mx-auto">
            <Sparkles size={20} className="text-blue-400 animate-pulse" />
          </div>
          <p className="text-sm text-white/40">Starting your onboarding...</p>
        </div>
      </div>
    );
  }

  if (startError) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center" data-testid="onboarding-error">
        <div className="text-center space-y-4 max-w-sm">
          <p className="text-red-400 text-sm">{startError}</p>
          <button
            onClick={() => { setStartError(null); startMutation.mutate(); }}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white flex flex-col" data-testid="onboarding-chat">
      {showPaywall && !pilotMode && (
        <PaywallModal
          onClose={() => setShowPaywall(false)}
          onSkip={handleSkipPaywall}
        />
      )}

      {/* Header */}
      <div className="border-b border-white/5 px-4 py-3 flex items-center gap-3 shrink-0" data-testid="onboarding-header">
        <div className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/25 flex items-center justify-center">
          <Sparkles size={15} className="text-blue-400" />
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-blue-400">Draper</p>
          <p className="text-[10px] text-white/30">AI Chief Marketing Officer</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          <span className="text-[10px] text-white/30">Setting up your workspace</span>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-6 space-y-4"
        data-testid="onboarding-messages"
      >
        {messages.map((msg, i) =>
          msg.role === "user"
            ? <UserMessage key={`${msg.ts}-${i}`} msg={msg} />
            : <DraperMessage key={`${msg.ts}-${i}`} msg={msg} />
        )}
        {isTyping && <TypingIndicator />}

        {isComplete && (
          <div className="mt-6 text-center" data-testid="onboarding-complete">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-xs font-bold mb-4">
              <CheckCircle2 size={12} /> Setup Complete
            </div>
            <p className="text-sm text-white/40 mb-4 max-w-md mx-auto">
              Draper is now drafting your business knowledge page, a starting strategy and Harry's watch list for you to review. Nothing is written, posted or sent without your approval.
            </p>
            <button
              data-testid="button-go-live"
              onClick={handleGoLive}
              className="flex items-center gap-2 px-8 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase tracking-wider text-sm rounded-xl transition-colors mx-auto"
            >
              {pilotMode ? "Open my workspace" : "Go Live"} <ArrowRight size={16} />
            </button>
            {!pilotMode && (
              <button
                data-testid="button-explore-app"
                onClick={handleSkipPaywall}
                className="block mt-3 text-[11px] text-white/20 hover:text-white/40 transition-colors mx-auto"
              >
                Explore the app first →
              </button>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      {!isComplete && (
        <div className="border-t border-white/5 px-4 py-3 shrink-0" data-testid="onboarding-input-area">
          <div className="flex items-end gap-2 max-w-2xl mx-auto">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={workspaceId ? "Reply to Draper..." : "Starting up..."}
              disabled={!workspaceId || sendMutation.isPending || isTyping}
              rows={1}
              className="flex-1 resize-none px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-blue-500/40 disabled:opacity-50 transition-colors"
              style={{ maxHeight: "120px" }}
              data-testid="input-onboarding-message"
            />
            <button
              onClick={sendMessage}
              disabled={!input.trim() || !workspaceId || sendMutation.isPending || isTyping}
              className="w-10 h-10 flex items-center justify-center bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-xl transition-all shrink-0"
              data-testid="button-send-onboarding"
            >
              {sendMutation.isPending || isTyping
                ? <Loader2 size={14} className="animate-spin" />
                : <Send size={14} />
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
